
import { computed, defineComponent, ref, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router/composables";
import { getModule } from "vuex-module-decorators";
import { useMediaQuery } from "@vueuse/core";
import { Constants } from "@app/constants";
import CiButton from "@components/base/button.vue";
import useHideBodyScroll from "@composables/hideBodyScroll";
import { ROUTENAMES } from "@models/constants/routes";
import { apiService } from "@services/api.service";
import { ProductsStoreModule } from "@stores/products.store";
import { Emitter } from "@utils/events";

const ProductsStore = getModule(ProductsStoreModule);

export default defineComponent({
  name: "CiProductsComparisonCart",
  components: {
    "ci-button": CiButton,
    "ci-comparison-list-tile": () => import("@components/products/comparison-list-tile.vue"),
  },
  props: {
    autoShowVariable: {
      type: String,
      default: "",
    },
  },
  emits: ["comparison-started"],
  setup(props, { emit }) {
    const router = useRouter();
    const { hideScroll, showScroll } = useHideBodyScroll();

    const show = ref(false);
    const openComparisonCartTimeout = ref();

    const comparisonProducts = computed<Products.TableData[]>(() => ProductsStore.comparisonProducts);

    const startComparison = async () => {
      const response = await apiService.comparisons.create({
        product_ids: comparisonProducts.value.map((product) => product.id),
      });
      emit("comparison-started");
      await router.push({ name: ROUTENAMES.COMPARISONS_SHOW, params: { id: response.data.slug } });
    };

    const toggleShow = () => {
      show.value = !show.value;
    };

    const isMobile = useMediaQuery(`(max-width: ${Constants.breakpoints.lg}px`);

    watch(isMobile, (value) => {
      if (!show.value) return;
      if (value) {
        hideScroll();
      } else {
        showScroll();
      }
    });

    watch(show, (value) => {
      if (!isMobile.value) return;
      if (value) {
        hideScroll();
      } else {
        showScroll();
      }
    });

    onMounted(() => {
      Emitter.on("ci::comparison-cart::added", () => {
        if (comparisonProducts.value.length === 1) show.value = true;
      });

      if (props.autoShowVariable && localStorage.getItem(props.autoShowVariable) !== "true") {
        localStorage.setItem(props.autoShowVariable, "true");
        openComparisonCartTimeout.value = setTimeout(() => (show.value = true), 3000);
      }
    });

    onUnmounted(() => {
      if (show.value) showScroll();
      clearTimeout(openComparisonCartTimeout.value);
    });
    return {
      show,
      comparisonProducts,
      startComparison,
      toggleShow,
    };
  },
});
